import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';
/**
 * Split view, not overlay (README). The list collapses to a compact column while
 * the panel opens inline on the right. On open the panel content fades in and
 * slides left 16px (`.tl-in`, `--fade-in`); the list simply reflows — no snapshot
 * or crossfade of the previous layout. Changing `panelKey` while the panel is
 * already open swaps the panel content only, with no animation and no remount.
 *
 * `rail` inverts the default: a narrow left column (widgets) and a wide right
 * canvas. `panelTop` (KPI tiles, …) stays mounted across row changes.
 */
export function SplitPanel({ open, list, panel, panelTop, panelKey, panelMax = 520, panelFrac = 0.55, maximised = false, rail = false, railMax = 340, panelBg = '#fff', listGap = 32, style, }) {
    const prevKey = useRef(panelKey);
    const prevOpen = useRef(open);
    /** panelKey that was mounted by the last open toggle; only that key gets the fade-in. */
    const fadeKey = useRef(open ? panelKey : null);
    const mobile = useMediaQuery('(max-width: 900px)');
    const [vw, setVw] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
    const rootRef = useRef(null);
    const [measured, setMeasured] = useState(0);
    useEffect(() => {
        const r = () => setVw(window.innerWidth);
        window.addEventListener('resize', r);
        return () => window.removeEventListener('resize', r);
    }, []);
    useLayoutEffect(() => {
        const el = rootRef.current;
        if (!el)
            return;
        const read = () => setMeasured(el.clientWidth);
        read();
        if (typeof ResizeObserver === 'undefined')
            return; // jsdom; the window resize above still re-renders
        const ro = new ResizeObserver(read);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    useEffect(() => {
        const toggled = prevOpen.current !== open;
        const keyChanged = prevKey.current !== panelKey;
        prevOpen.current = open;
        prevKey.current = panelKey;
        if (toggled)
            fadeKey.current = open ? panelKey : null;
        else if (keyChanged)
            fadeKey.current = null; // row-to-row with the panel open: content swap only
    }, [open, panelKey]);
    // Fade in only on the render that opens the panel (refs are updated after commit) or while that same key is still mounted.
    const fadeIn = open && (!prevOpen.current || fadeKey.current === panelKey);
    /**
     * The panel is a fraction of the room this split actually has, so measure it. Deriving it from the window
     * less an assumed 232px sidebar left the panel the same width when an app collapses its sidebar — the whole
     * gain went to the list — and is wrong for any shell whose sidebar is not 232px. Until the first measurement
     * (and under jsdom, where clientWidth is 0) the old estimate stands in.
     */
    const avail = measured || Math.max(0, vw - (vw > 900 ? 232 : 0));
    const frac = maximised ? Math.max(panelFrac, 0.88) : panelFrac;
    const max = maximised ? Math.max(panelMax, 1600) : panelMax;
    const panelPx = open ? Math.round(Math.min(max, avail * frac)) : 0;
    const railPx = !open ? 0 : maximised ? 0 : Math.min(railMax, Math.max(240, Math.round(avail * 0.28)));
    const columns = mobile
        ? 'minmax(0,1fr)'
        : rail
            ? (open ? `${railPx}px minmax(0,1fr)` : 'minmax(0,1fr)')
            : `minmax(0,1fr) ${panelPx}px`;
    const panelStyle = mobile && open
        ? { position: 'fixed', inset: 0, zIndex: 30, height: '100vh', borderLeft: 0 }
        : { minWidth: 0, overflow: 'hidden', height: '100%', borderLeft: open ? '1px solid var(--border)' : 0 };
    const bg = open && !mobile
        ? (rail
            ? `linear-gradient(to right, var(--bg) 0, var(--bg) ${railPx}px, ${panelBg} ${railPx}px)`
            : `linear-gradient(to right, var(--bg) 0, var(--bg) calc(100% - ${panelPx}px), ${panelBg} calc(100% - ${panelPx}px))`)
        : 'var(--bg)';
    return (_jsxs("div", { ref: rootRef, "data-split": true, "data-rail": rail ? '1' : '0', "data-maximised": maximised && open ? '1' : '0', style: { position: 'relative', display: 'grid', flex: 1, minHeight: 0, gridTemplateColumns: columns, background: bg, ...style }, children: [_jsx("div", { "data-pad": true, style: { padding: `16px ${open && !mobile ? 12 : listGap}px 16px ${rail ? 16 : listGap}px`, display: rail && maximised && !mobile ? 'none' : 'flex', flexDirection: 'column', gap: 10, minWidth: 0, minHeight: 0, overflow: 'auto' }, children: list }), _jsx("div", { "data-panel": true, "data-open": open ? '1' : '0', style: { ...panelStyle, background: panelBg, display: open ? 'flex' : 'none', flexDirection: 'column' }, children: open && (_jsxs(_Fragment, { children: [panelTop, _jsx("div", { className: fadeIn ? 'tl-in' : undefined, style: { width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }, children: panel })] })) })] }));
}
//# sourceMappingURL=SplitPanel.js.map